import type { UseFormProps } from 'react-hook-form';

/*
    default config parameters for forms
    Full config values: https://react-hook-form.com/docs/useform
*/
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const form: Partial<UseFormProps<any>> = {
  shouldFocusError: false,
  mode: 'onSubmit',
  reValidateMode: 'onSubmit',
};

const normalizeDomain = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/^@/, '').replace(/\.$/, '');
};

const normalizeTenant = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase();
};

type ShareAllowedEmailDomainsGroup = {
  tenants?: unknown;
  domains?: unknown;
};

type ShareAllowedEmailDomainsPayload = {
  groups?: unknown;
};

const parseShareAllowedEmailDomainsByTenant = (
  value?: string | null,
): Record<string, string[]> => {
  const raw = String(value || '').trim();
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as ShareAllowedEmailDomainsPayload;
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.groups)) {
      return {};
    }

    const byTenant: Record<string, string[]> = {};
    for (const group of parsed.groups as ShareAllowedEmailDomainsGroup[]) {
      if (!group || typeof group !== 'object') continue;

      const tenants = Array.isArray(group.tenants)
        ? group.tenants.map(normalizeTenant).filter(Boolean)
        : [];
      const domains = Array.isArray(group.domains)
        ? group.domains.map(normalizeDomain).filter(Boolean)
        : [];

      if (tenants.length === 0 || domains.length === 0) continue;

      for (const tenant of tenants) {
        if (!byTenant[tenant]) byTenant[tenant] = [];
        for (const domain of domains) {
          if (!byTenant[tenant].includes(domain)) {
            byTenant[tenant].push(domain);
          }
        }
      }
    }

    return byTenant;
  } catch (_err) {
    return {};
  }
};

const config = {
  apiUrl: process.env.REACT_APP_API_URL || null,
  disableAuth: process.env.REACT_APP_DISABLE_AUTH === 'true',
  shareAllowedEmailDomainsByTenant: parseShareAllowedEmailDomainsByTenant(
    process.env.REACT_APP_SHARE_ALLOWED_EMAIL_DOMAINS,
  ),
  urls: {
    landing: '',
    landingAccounts: '',
    signin: 'signin',
    summary: 'summary',
    declaration: 'declaration',
    declarationSuccess: 'declaration/success',
    resetPasswordEmail: 'reset-password-email',
    resetPassword: 'reset-password',
    enterAppMFACode: 'enter-app-mfa-code',
    setNewPassword: 'set-new-password',
    defaultError: 'error',
  },
  form,
} as const;

export const getShareAllowedEmailDomainsForTenant = (tenant?: string | null): string[] => {
  const normalizedTenant = normalizeTenant(tenant);
  if (!normalizedTenant) return [];
  const domains = config.shareAllowedEmailDomainsByTenant[normalizedTenant];
  return Array.isArray(domains) ? domains : [];
};

export default config;
